import { EditorContent } from '@tiptap/react';
import * as Api from '@ViewModels';
import * as React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { type IMergeFieldToken, useRichTextEditor } from '@levitate/rich-text';
import { createContentStateWithHtmlStringValue } from '../../../models/UiUtils';
import { THEME } from '../../components-v2/Theme/theme';
import {
	editorHtmlToStoredHtml,
	FILE_SEND_PLACEHOLDER_VALUE,
	FILE_SEND_SYMBOLS,
	storedHtmlToEditorHtml,
} from './textCampaignComposerContent';

interface ITextCampaignComposerProps {
	contentState: Api.IRichContentEditorState;
	tokens: IMergeFieldToken[];
	readOnly?: boolean;
	placeholder?: string;
	onContentStateChanged?: (state: Api.IRichContentEditorState) => void;
}

export interface ITextCampaignComposerHandle {
	insertTokenBySymbol: (symbol: string) => void;
	removeFileSendTokens: () => void;
	hasFileSendToken: () => boolean;
	insertText: (text: string) => void;
	focus: () => void;
}

const isTextNode = (node: unknown): node is { isText: true; text?: string } =>
	!!node && (node as { isText?: boolean }).isText === true;

const isFileSendNode = (node: { type?: { name?: string }; attrs?: { symbol?: string; value?: string } }): boolean =>
	node?.type?.name === 'mergeField' &&
	(FILE_SEND_SYMBOLS.includes(node.attrs?.symbol ?? '') || node.attrs?.value === FILE_SEND_PLACEHOLDER_VALUE);

function contentStateToEditorHtml(state: Api.IRichContentEditorState | undefined, tokens: IMergeFieldToken[]): string {
	const raw = state?.getRawRichTextContent();
	const html = raw ? (Api.getContentHtmlStringValueFromRawRichTextContentState(raw) ?? '') : '';
	return storedHtmlToEditorHtml(html, tokens);
}

export const TextCampaignComposer = forwardRef<ITextCampaignComposerHandle, ITextCampaignComposerProps>(
	({ contentState, tokens, readOnly, placeholder, onContentStateChanged }, ref) => {
		const onChangeRef = useRef(onContentStateChanged);
		onChangeRef.current = onContentStateChanged;
		const lastEmittedRef = useRef<Api.IRichContentEditorState | null>(contentState ?? null);

		// eslint-disable-next-line react-hooks/exhaustive-deps
		const initialContent = useMemo(() => contentStateToEditorHtml(contentState, tokens), []);

		const editor = useRichTextEditor({
			initialContent,
			starterKitOptions: {
				bold: false,
				italic: false,
				strike: false,
				code: false,
				blockquote: false,
				heading: false,
				horizontalRule: false,
				bulletList: false,
				orderedList: false,
				listItem: false,
				codeBlock: false,
				paragraph: { HTMLAttributes: { style: 'margin:0;padding:0;' } },
			},
			mergeField: { tokens },
			onUpdate(editorInstance) {
				const storedHtml = editorHtmlToStoredHtml(editorInstance.getHTML() as string);
				const state = createContentStateWithHtmlStringValue(storedHtml);
				lastEmittedRef.current = state;
				onChangeRef.current?.(state);
			},
		});

		useEffect(() => {
			editor?.setEditable(!readOnly);
		}, [editor, readOnly]);

		// Sync externally-driven content (async draft load, template duplicate) without clobbering the
		// user's own edits: a state we emitted is the same object reference we get back, so skip it.
		useEffect(() => {
			if (!editor || contentState === lastEmittedRef.current) {
				return;
			}
			editor.commands.setContent(contentStateToEditorHtml(contentState, tokens), { emitUpdate: false });
			lastEmittedRef.current = contentState;
		}, [editor, contentState, tokens]);

		useImperativeHandle(
			ref,
			() => ({
				insertTokenBySymbol(symbol) {
					if (!editor) {
						return;
					}
					const token = tokens.find(t => t.symbol === symbol);
					if (!token) {
						return;
					}
					const attrs = token.value
						? { text: token.text, value: token.value }
						: { text: token.text, symbol: token.symbol };
					// Pad the pill with a space on each side so the resolved link never glues to adjacent text.
					const { $from, $to } = editor.state.selection;
					const endsWithSpace = (node: typeof $from.nodeBefore) => isTextNode(node) && /\s$/.test(node.text ?? '');
					const startsWithSpace = (node: typeof $to.nodeAfter) => isTextNode(node) && /^\s/.test(node.text ?? '');
					const chain = editor.chain().focus();
					if ($from.nodeBefore && !endsWithSpace($from.nodeBefore)) {
						chain.insertContent({ type: 'text', text: ' ' });
					}
					chain.insertMergeField(attrs);
					if (!startsWithSpace($to.nodeAfter)) {
						chain.insertContent({ type: 'text', text: ' ' });
					}
					chain.run();
				},
				removeFileSendTokens() {
					if (!editor) {
						return;
					}
					const { state, dispatch } = editor.view;
					const ranges: { from: number; to: number }[] = [];
					state.doc.descendants((node, pos) => {
						if (isFileSendNode(node)) {
							ranges.push({ from: pos, to: pos + node.nodeSize });
						}
					});
					if (!ranges.length) {
						return;
					}
					const tr = state.tr;
					for (const { from, to } of [...ranges].reverse()) {
						tr.delete(from, to);
					}
					dispatch(tr);
				},
				hasFileSendToken() {
					if (!editor) {
						return false;
					}
					let found = false;
					editor.state.doc.descendants(node => {
						if (isFileSendNode(node)) {
							found = true;
						}
					});
					return found;
				},
				insertText(text) {
					editor?.chain().focus().insertContent(text).run();
				},
				focus() {
					editor?.commands.focus();
				},
			}),
			[editor, tokens]
		);

		const isEmpty = editor?.isEmpty ?? true;

		return (
			<div style={{ position: 'relative', width: '100%' }}>
				{isEmpty && placeholder ? (
					<span
						style={{
							color: THEME.colors.text.light,
							fontSize: THEME.typography.fontSizes.sm,
							left: 0,
							pointerEvents: 'none',
							position: 'absolute',
							top: 0,
						}}
					>
						{placeholder}
					</span>
				) : null}
				<EditorContent editor={editor} style={{ border: 'none', outline: 'none', width: '100%', minHeight: '1em' }} />
			</div>
		);
	}
);

TextCampaignComposer.displayName = 'TextCampaignComposer';
